import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import SnackbarContext from '@/contexts/Snackbar/SnackbarContext';
import type { SnackbarOpenOptions } from '@/contexts/Snackbar/ISnackbarContext';
import type SnackbarState from '@/contexts/Snackbar/SnackbarState';
import { SnackbarType } from '@/contexts/Snackbar/SnackbarType';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import userMessage from '@/utils/userMessage';

import MUISnackbar from '@mui/material/Snackbar';

function SnackbarProvider({ children }: { children: ReactNode }) {
  const dedupeTimestamps = useRef(new Map<string, number>());
  const [state, setState] = useState<SnackbarState>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
    message: '',
    type: SnackbarType.Success,
    title: null,
  });

  const open = useCallback(
    ({
      open = true,
      message,
      title,
      vertical = 'top',
      horizontal = 'center',
      type = SnackbarType.Success,
      dedupeKey,
      dedupeWindowMs = 5_000,
    }: SnackbarOpenOptions) => {
      if (dedupeKey) {
        const now = Date.now();
        const lastOpenedAt = dedupeTimestamps.current.get(dedupeKey);
        if (lastOpenedAt !== undefined && now - lastOpenedAt < dedupeWindowMs) {
          return;
        }
        dedupeTimestamps.current.set(dedupeKey, now);
      }

      setState({
        open,
        message: userMessage(message),
        vertical,
        horizontal,
        type,
        title,
      });
    },
    []
  );

  const close = useCallback(() => {
    setState({
      open: false,
      message: '',
      vertical: 'top',
      horizontal: 'center',
      type: SnackbarType.Success,
      title: null,
    });
  }, []);

  const value = useMemo(
    () => ({
      open,
      close,
      state,
    }),
    [close, open, state]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <MUISnackbar
        anchorOrigin={{
          vertical: state.vertical,
          horizontal: state.horizontal,
        }}
        open={state.open}
        onClose={close}
        autoHideDuration={6000}
        transitionDuration={
          import.meta.env.VITE_STOP_ANIMATIONS === 'true' ? 0 : undefined
        }
      >
        <Alert
          severity={state.type}
          variant="standard"
          className="w-full"
          role={state.type === SnackbarType.Error ? 'alert' : 'status'}
        >
          {state.title && <Typography>{state.title}</Typography>}
          {state.message}
        </Alert>
      </MUISnackbar>
    </SnackbarContext.Provider>
  );
}

export default SnackbarProvider;
